import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";
import { useTopics } from "@/hooks/useTopics";
import { useHotTopics } from "@/hooks/useHotTopics";
import { useMostCommentedTopics } from "@/hooks/useMostCommentedTopics";
import { useMostViewedTopics } from "@/hooks/useMostViewedTopics";
import { useCategories } from "@/hooks/useCategories";
import { useForumSettings } from "@/hooks/useForumSettings";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { TopicTable } from "./TopicTable";
import { ReportModal } from "./ReportModal";
import { AdSlot300x250 } from "../ads/AdSlot300x250";
import GPTResponsiveAd from "../ads/GPTResponsiveAd";
import { formatCategoryDisplayName } from "@/utils/categoryHelpers";

// Parent category ID to slug mapping for Level 2 URLs
const PARENT_SLUG_MAP: Record<string, string> = {
  "80bd37c9-9d89-404d-82e2-0f47c2e05af5": "youth-soccer-forum-canada",
  "fc0f3c04-085a-455d-966e-64200bee6b1b": "youth-soccer-forum-usa",
};

export const ForumHome = () => {
  const { getSetting } = useForumSettings();
  const [reportModal, setReportModal] = useState<{
    isOpen: boolean;
    topicId?: string;
  }>({
    isOpen: false,
  });

  // Sort & pagination state for the main topics list
  const [searchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "";
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Reset page when sort changes
  useEffect(() => {
    setPage(1);
  }, [sort]);

  // Choose the appropriate hook based on the `sort` query param
  // - hot => useHotTopics
  // - top => useMostCommentedTopics (alternative: useMostViewedTopics)
  // - new => useTopics(..., 'created_at')
  // - default/unknown => useTopics(..., 'last_reply_at')
  const isHot = sort === "hot";
  const isTop = sort === "top";
  const isNew = sort === "new";

  const hotHook = useHotTopics();
  const mostCommentedHook = useMostCommentedTopics();
  const mostViewedHook = useMostViewedTopics();
  const topicsHook = useTopics({
    page,
    limit: ITEMS_PER_PAGE,
    sortBy: isNew ? "newest" : "latest",
  });

  // Select active hook result
  const active = isHot ? hotHook : isTop ? mostCommentedHook : topicsHook;

  // Normalize topics array to the shape TopicTable expects
  // Extract the actual data from the query result
  const activeQueryData: any = active.data;
  const rawTopics = activeQueryData?.topics || activeQueryData?.data || [];
  const topics = (rawTopics || []).map((topic: any) => ({
    // prefer existing nested shapes from enriched topics, otherwise use flat fields returned by other RPCs
    ...topic,
    id: topic.id,
    title: topic.title,
    slug: topic.slug || topic.topic_slug || topic.topic?.slug || "",
    username: topic.profiles?.username || topic.username || null,
    avatar_url: topic.profiles?.avatar_url || topic.avatar_url || null,
    category_name:
      topic.categories?.name ||
      topic.category_name ||
      topic.category ||
      "General",
    category_color:
      topic.categories?.color || topic.category_color || "#3b82f6",
    category_slug: topic.categories?.slug || topic.category_slug || "",
    hot_score: topic.hot_score || 0,
    last_post_id: topic.last_post_id || null,
    parent_category_id:
      topic.categories?.parent_category_id || topic.parent_category_id || null,
    parent_category_slug: topic.parent_category_slug || null,
    last_reply_username: topic.last_reply_username || null,
    last_reply_avatar: topic.last_reply_avatar || null,
  }));

  const activeLoading = active.isLoading;
  const activeTotalPages = Math.ceil(((activeQueryData?.total || activeQueryData?.totalCount || 0) / ITEMS_PER_PAGE));
  const activeTotalCount = activeQueryData?.total || activeQueryData?.totalCount || 0;

  const { data: level1Forums } = useCategories(null, 1); // Only Level 1 forums
  const { data: level2Forums } = useCategories(undefined, 2); // Province/State forums

  const handleReport = (topicId: string) => {
    setReportModal({
      isOpen: true,
      topicId,
    });
  };

  return (
    <div className="forum-spacing relative w-full overflow-x-hidden max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Minor Soccer Talks – Youth Soccer Forum & Community
        </h1>
        <p className="text-muted-foreground">
          Minor Soccer Talks is the go-to community for youth soccer players, parents, and coaches to connect, share insights, and discuss everything from grassroots leagues to elite tournaments. Explore training tips, player development, team discussions, and the latest in youth soccer news.
        </p>
      </div>

      {/* Leaderboard Top */}
      <GPTResponsiveAd slotId="responsive-1" />

      {/* Topics Section (sorted by ?sort=) */}
      <div className="forum-spacing">
        <TopicTable
          topics={topics}
          loading={activeLoading}
          showCategory={true}
        />
        {topics.length > 0 && (
          <PaginationControls
            currentPage={page}
            totalPages={activeTotalPages}
            totalItems={activeTotalCount}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
            loading={activeLoading}
          />
        )}
      </div>

      {/* Ad Slot 300x250 */}
      <AdSlot300x250 slotId="300x250-4" />

      {/* Forums Section */}
      <div className="forum-spacing">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">
            Browse Main Forums
          </h2>
        </div>

        <div
          className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
        >
          {level1Forums?.map((forum) => (
            <Link
              key={forum.id}
              to={`/${forum.slug}`}
              className="block"
            >
              <Card className="p-4 hover:bg-forum-row-hover transition-colors cursor-pointer border border-forum-border-subtle h-[140px] flex flex-col">
                <div className="flex items-center space-x-2 mb-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: forum.color }}
                  />
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-colors text-sm line-clamp-1">
                    {formatCategoryDisplayName(forum.name)}
                  </h3>
                </div>
                {forum.description && (
                  <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
                    {forum.description}
                  </p>
                )}
              </Card>
            </Link>
          ))}
        </div>

        {(!level1Forums || level1Forums.length === 0) && (
          <Card className="p-6 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-base font-semibold mb-2">
              No forums available
            </h3>
            <p className="text-muted-foreground text-sm">
              Forums will appear here once they are created.
            </p>
          </Card>
        )}
      </div>

      {/* Ad Slot 300x250 */}
      <AdSlot300x250 slotId="300x250-5" />

      {/* Province/State Forums Section */}
      <div className="forum-spacing">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">
            Browse Province / State Forums
          </h2>
        </div>

        {level2Forums && level2Forums.length > 0 ? (
          <div className="forum-spacing">
            {(() => {
              // Filter out tournament forums and group by country using parent_category_id
              const canadianForums = level2Forums
                .filter(
                  (forum) =>
                    forum.parent_category_id ===
                    "80bd37c9-9d89-404d-82e2-0f47c2e05af5"
                )
                .sort((a, b) => (a.region || "").localeCompare(b.region || ""));

              const usaForums = level2Forums
                .filter(
                  (forum) =>
                    forum.parent_category_id ===
                    "fc0f3c04-085a-455d-966e-64200bee6b1b"
                )
                .sort((a, b) => (a.region || "").localeCompare(b.region || ""));

              const countries = [];
              if (canadianForums.length > 0) {
                countries.push({ name: "Canada", forums: canadianForums });
              }
              if (usaForums.length > 0) {
                countries.push({ name: "USA", forums: usaForums });
              }

              return countries.map((country, index) => (
                <div key={country.name}>
                  <div className="forum-spacing">
                    <h3 className="text-base font-semibold text-foreground border-b pb-1 forum-header">
                      {country.name}
                    </h3>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {country.forums.map((forum) => {
                        const parentSlug = forum.parent_category_id 
                          ? PARENT_SLUG_MAP[forum.parent_category_id] 
                          : '';
                        const forumUrl = parentSlug 
                          ? `/${parentSlug}/${forum.slug}` 
                          : `/${forum.slug}`;
                        
                        return (
                          <Link
                            key={forum.id}
                            to={forumUrl}
                            className="block"
                          >
                          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer h-[140px] flex flex-col">
                            <div className="flex items-center space-x-2 mb-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: forum.color }}
                              />
                              <h4 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">
                                {formatCategoryDisplayName(forum.name)}
                              </h4>
                            </div>
                            {forum.description && (
                              <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
                                {forum.description}
                              </p>
                            )}
                          </Card>
                        </Link>
                        );
                      })}
                    </div>
                  </div>
                  {index === 0 && <AdSlot300x250 slotId="300x250-6" />}
                </div>
              ));
            })()}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No province/state forums available
            </h3>
            <p className="text-muted-foreground">
              Province and state forums will appear here once they are created.
            </p>
          </Card>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModal.isOpen}
        onClose={() => setReportModal({ isOpen: false })}
        topicId={reportModal.topicId}
        contentType="topic"
      />
    </div>
  );
};
